import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpDown, CircleAlert, ClipboardList, Download, History, Loader2, PackagePlus, RefreshCw, Search, Settings2, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { usePermission, useTenant } from "@/components/AppShell";
import { useModuleFeature } from "@/components/ModuleGate";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, canManage, isDemo, REACH_STATUS_LABEL, type ProductReplenishment, type ReachItem, type ReachQuery, type ReachStatus, type ReplenishmentSettings } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

const PAGE_SIZE = 100;
const STATUS_META: Record<ReachStatus, { dot: string; bar: string; tone: "danger" | "warning" | "info" | "success" | "neutral" }> = {
  out: { dot: "bg-danger", bar: "bg-danger", tone: "danger" },
  critical: { dot: "bg-danger", bar: "bg-danger", tone: "danger" },
  reorder: { dot: "bg-warning", bar: "bg-warning", tone: "warning" },
  ok: { dot: "bg-success", bar: "bg-success", tone: "success" },
  overstock: { dot: "bg-info", bar: "bg-info", tone: "info" },
  no_sales: { dot: "bg-fg-subtle", bar: "bg-fg-subtle", tone: "neutral" },
  ignored: { dot: "bg-border-strong", bar: "bg-border", tone: "neutral" },
};
const CHIPS: (ReachStatus | "action" | "all")[] = ["action", "out", "critical", "reorder", "ok", "overstock", "no_sales", "ignored", "all"];
const chipLabel = (k: ReachStatus | "action" | "all") => (k === "action" ? "Handlungsbedarf" : k === "all" ? "Alle" : REACH_STATUS_LABEL[k]);
const days = (n: number | null) => (n == null ? "—" : n >= 999 ? "> 999 T." : `${fmtNumber(Math.floor(n))} T.`);

const deDate = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Herkunft der Absatzzahlen: Verkaufshistorie aus Shopify (vor dem Start mit Logiful) + eigene Aufträge.
 * Ohne Historie wäre die Reichweite erst nach Wochen aussagekräftig – deshalb wird sie automatisch geholt.
 */
function SalesHistoryBar() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["sales-history", tenantId],
    queryFn: () => api.stock.salesHistory(tenantId),
    refetchInterval: (query) => (query.state.data?.shops.some((s) => s.state && ["waiting", "exporting", "importing"].includes(s.state.status)) ? 5000 : false),
  });
  const load = useMutation({
    mutationFn: (channelId: string) => api.stock.loadSalesHistory(tenantId, channelId),
    onSuccess: () => { toast.success("Verkaufshistorie wird geladen", { description: msg("Dauert je nach Shop wenige Minuten. Es entstehen keine Aufträge.") }); void qc.invalidateQueries({ queryKey: ["sales-history", tenantId] }); },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Nicht gestartet") : "Nicht gestartet"),
  });
  const wasRunning = q.data?.shops.some((s) => s.state && s.state.status !== "done" && s.state.status !== "failed");
  useEffect(() => {
    // fertig geworden → Reichweite mit den neuen Zahlen neu laden
    if (wasRunning === false) void qc.invalidateQueries({ queryKey: ["reach", tenantId] });
  }, [wasRunning, qc, tenantId]);
  const shops = q.data?.shops.filter((s) => s.active || s.state) ?? [];
  if (!shops.length) return null;
  return (
    <div className="space-y-2">
      {shops.map((s) => {
        const st = s.state;
        const running = st && ["waiting", "exporting", "importing"].includes(st.status);
        // Shopify liefert ohne Berechtigung „read_all_orders“ nur 60 Tage
        const age = st?.firstDay ? (Date.parse(st.finishedAt ?? new Date().toISOString()) - Date.parse(st.firstDay)) / 86_400_000 : 0;
        const limited = st?.status === "done" && !!st.orders && age >= 55 && age <= 62;
        const reload = canManage(role) && s.active && !running && (
          <button onClick={() => load.mutate(s.channelId)} disabled={load.isPending} className="inline-flex items-center gap-1 text-[12px] font-medium text-fg-muted hover:text-fg underline-offset-2 hover:underline">
            <RefreshCw className="size-3" /> {st ? t("Neu laden") : t("Jetzt laden")}
          </button>
        );
        return (
          <div key={s.channelId} className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-2 text-[12.5px]", st?.status === "failed" ? "border-danger/30 bg-danger-soft/40" : "border-border bg-surface")}>
            {running ? <Loader2 className="size-4 animate-spin text-fg-subtle" /> : st?.status === "failed" ? <CircleAlert className="size-4 text-danger" /> : <History className="size-4 text-fg-subtle" />}
            <span className="min-w-0 flex-1">
              {!st && <>{t("Verkaufshistorie aus")} <b>{s.name}</b> {t("noch nicht geladen – die Reichweite rechnet bis dahin nur mit Aufträgen seit dem Start.")}</>}
              {running && <>{t("Verkaufshistorie aus")} <b>{s.name}</b> {t("wird geladen")}{st!.exported ? t(" – {v} Datensätze exportiert", { v: fmtNumber(st!.exported) }) : " …"}</>}
              {st?.status === "failed" && <>{t("Verkaufshistorie aus")} <b>{s.name}</b> {t("nicht geladen:")} {st.error}</>}
              {st?.status === "done" && (st.orders ? (
                <>{t("Absatz enthält die Shopify-Historie von")} <b>{s.name}</b> {t("ab")} {deDate(st.firstDay!)} ({fmtNumber(st.orders)} {t("Bestellungen)")}{s.connectFrom ? <>{t(", ab")} {deDate(s.connectFrom)} {t("zählen die Aufträge in Connect")}</> : null}.</>
              ) : <>{t("In")} <b>{s.name}</b> {t("wurden keine Bestellungen der letzten zwei Jahre gefunden.")}</>)}
              {limited && <span className="block text-fg-subtle">{t("Shopify hat nur die letzten ~60 Tage geliefert. Für die volle Historie (Saisonvergleich) braucht die Shopify-App zusätzlich die Berechtigung „read_all_orders“.")}</span>}
            </span>
            {reload}
          </div>
        );
      })}
    </div>
  );
}

/** Bestandsreichweite & Nachbestell-Assistent (Reiter unter „Bestände“) */
export function ReachPanel() {
  const { tenantId, role } = useTenant();
  const mayInbound = usePermission()("inbounds");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") as ReachQuery["filter"] | null) ?? "action";
  const supplier = params.get("supplier") ?? undefined;
  const windowDays = (Number(params.get("window")) || undefined) as ReachQuery["window"];
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [term, setTerm] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [sort, setSort] = useState<{ key: NonNullable<ReachQuery["sort"]>; dir: "asc" | "desc" }>({ key: "urgency", dir: "asc" });
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [term, filter, supplier, windowDays, sort.key, sort.dir]);
  const query: ReachQuery = { ...(term ? { search: term } : {}), filter: filter ?? "action", ...(supplier ? { supplier } : {}), ...(windowDays ? { window: windowDays } : {}), sort: sort.key, dir: sort.dir, page, pageSize: PAGE_SIZE };
  const q = useQuery({ queryKey: ["reach", tenantId, query], queryFn: () => api.stock.reach(tenantId, query), placeholderData: keepPreviousData, refetchInterval: 5 * 60_000 });
  const set = (k: string, v: string | null) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", "reach"); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  // Auswahl + abweichende Mengen (Vorschlag ist vorbelegt)
  const [picked, setPicked] = useState<Map<string, { sku: string; qty: number }>>(new Map());
  const [qtyEdit, setQtyEdit] = useState<Map<string, number>>(new Map());
  const qtyOf = (r: ReachItem) => qtyEdit.get(r.productId) ?? r.suggested;
  const toggle = (r: ReachItem, on: boolean) =>
    setPicked((m) => {
      const n = new Map(m);
      if (on) n.set(r.productId, { sku: r.sku, qty: Math.max(1, qtyOf(r) || r.packSize || 1) });
      else n.delete(r.productId);
      return n;
    });
  const setQty = (r: ReachItem, v: number) => {
    setQtyEdit((m) => new Map(m).set(r.productId, v));
    setPicked((m) => (m.has(r.productId) ? new Map(m).set(r.productId, { sku: r.sku, qty: Math.max(1, v) }) : m));
  };
  const items = q.data?.items ?? [];
  const withSuggestion = items.filter((r) => r.suggested > 0);
  const allPicked = withSuggestion.length > 0 && withSuggestion.every((r) => picked.has(r.productId));
  const pickedUnits = [...picked.values()].reduce((n, x) => n + x.qty, 0);
  // Einkauf Pro: Bestellungen je Lieferant aus der Auswahl
  const purchasing = useModuleFeature("purchasing", "purchase_orders");
  const [askSupplier, setAskSupplier] = useState<string[] | null>(null);
  const order = useMutation({
    mutationFn: (supplierId: string | null) => api.purchasing.createDrafts(tenantId, { items: [...picked].map(([productId, x]) => ({ productId, quantity: x.qty })), supplierId }),
    onSuccess: (r) => {
      setAskSupplier(null);
      setPicked(new Map());
      toast.success(r.created.length === 1 ? "Bestellung angelegt" : `${r.created.length} Bestellungen angelegt`, { description: msg("Prüfen und an den Lieferanten senden.") });
      navigate(r.created.length === 1 ? `/purchasing?open=${r.created[0]!.id}` : "/purchasing");
    },
    onError: (e) => {
      const body = e instanceof ApiError ? (e.body as { error?: string; detail?: string[]; message?: string }) : null;
      if (body?.error === "no_supplier") setAskSupplier(body.detail ?? []);
      else toast.error("Nicht angelegt", { description: body?.message ?? "Fehler" });
    },
  });
  const toInbound = () => navigate(`/inbounds?new=1&skus=${encodeURIComponent([...picked.values()].map((x) => `${x.sku}:${x.qty}`).join(","))}`);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState<ReachItem | null>(null);
  const s = q.data?.settings;
  const counts = q.data?.counts;
  const countOf = (k: ReachStatus | "action" | "all") =>
    !counts ? null : k === "action" ? counts.out + counts.critical + counts.reorder : k === "all" ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[k];
  const pages = Math.max(1, Math.ceil((q.data?.total ?? 0) / PAGE_SIZE));
  const sortBy = (key: NonNullable<ReachQuery["sort"]>) => setSort((x) => ({ key, dir: x.key === key && x.dir === "asc" ? "desc" : "asc" }));
  const shownWindow = windowDays ?? s?.windowDays ?? 30;
  const selectCls = "h-8 rounded-lg border border-border bg-surface px-2.5 text-[12.5px] hover:border-border-strong";

  return (
    <div className="space-y-4">
      <SalesHistoryBar />
      {/* Status-Filter */}
      <div className="flex flex-wrap items-center gap-2">
        {CHIPS.map((k) => {
          const n = countOf(k);
          if (k === "ignored" && !n) return null;
          const active = filter === k;
          return (
            <button key={k} onClick={() => set("filter", k === "action" ? null : k)} className={cx("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12.5px] font-medium transition-colors", active ? "bg-ink text-ink-fg border-ink" : "bg-surface border-border hover:bg-surface-2")}>
              {k !== "all" && k !== "action" && <span className={cx("size-2 rounded-full", STATUS_META[k].dot)} />}
              {chipLabel(k)} <span className="tabular-nums opacity-70">{n ?? "·"}</span>
            </button>
          );
        })}
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Suchen: SKU, Artikel oder Lieferant …")} aria-label={t("Reichweite durchsuchen")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px]" />

      <div className="flex flex-wrap items-center gap-2">
        {(q.data?.suppliers.length ?? 0) > 0 && (
          <select aria-label={t("Lieferant")} value={supplier ?? ""} onChange={(e) => set("supplier", e.target.value || null)} className={cx(selectCls, supplier && "border-ink")}>
            <option value="">{t("Alle Lieferanten")}</option>
            {q.data!.suppliers.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        )}
        <select aria-label={t("Zeitraum für den Absatz")} value={windowDays ?? s?.windowDays ?? 30} onChange={(e) => set("window", e.target.value)} className={selectCls}>
          {[14, 30, 60, 90].map((d) => <option key={d} value={d}>{t("Ø der letzten")} {d} {t("Tage")}</option>)}
        </select>
        {s && (
          <button onClick={() => canManage(role) && setSettingsOpen(true)} className={cx("inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-border text-[12.5px] text-fg-muted", canManage(role) && "hover:bg-surface-2 hover:text-fg")} title={canManage(role) ? t("Standardwerte ändern") : t("Nur Administratoren können die Standardwerte ändern")}>
            <Settings2 className="size-3.5" /> {t("Lieferzeit")} {s.leadDays} {t("T. · Puffer")} {s.safetyDays} {t("T. · reicht")} {s.coverDays} T.
          </button>
        )}
        <a href={isDemo ? undefined : api.stock.reachCsvUrl(tenantId, query)} onClick={isDemo ? () => toast.info("Im Demo-Modus nicht verfügbar") : undefined} className="ml-auto">
          <Button size="sm" variant="ghost" icon={<Download className="size-3.5" />}>{t("Bestellvorschlag (CSV)")}</Button>
        </a>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-[12px] text-fg-subtle border-b border-border bg-surface-2/50">
              <tr>
                <th className="h-9 pl-4 pr-1 w-8">
                  <input type="checkbox" aria-label={t("Alle mit Vorschlag auswählen")} checked={allPicked} disabled={!withSuggestion.length} onChange={(e) => withSuggestion.forEach((r) => toggle(r, e.target.checked))} className="accent-ink" />
                </th>
                <Th label={t("Artikel")} onClick={() => sortBy("sku")} active={sort.key === "sku"} />
                <Th label={t("Absatz")} onClick={() => sortBy("daily")} active={sort.key === "daily"} tip={t("Ø verkaufte Stück je Tag ({v} volle Tage, Sets in Bestandteilen) – Linie: letzte 12 Wochen", { v: q.data?.settings.windowDays ?? 30 })} />
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Verkaufte Stück in den letzten {shownWindow} vollen Tagen (Sets in Bestandteilen, ohne Stornos) – darunter: letzte 90 Tage", { shownWindow })}><button onClick={() => sortBy("daily")} className={cx("inline-flex items-center gap-1 hover:text-fg", sort.key === "daily" && "text-fg")}>{t("Verkauft")} <ArrowUpDown className="size-3" /></button></Tip></th>
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Lager-verfügbar − im Portal reserviert")}><span>{t("Frei")}</span></Tip></th>
                <th className="h-9 px-3 font-medium text-right">{t("Zulauf")}</th>
                <Th label={t("Reichweite · bestellen")} onClick={() => sortBy("daysLeft")} active={sort.key === "daysLeft"} tip={t("Wie lange der freie Bestand beim aktuellen Absatz reicht – darunter: spätestens bestellen, damit die Ware vor dem Ausverkauf eingelagert ist (Lieferzeit + Puffer, Zulauf eingerechnet)")} />
                <th className="h-9 px-3 font-medium text-right"><Tip content={t("Deckt Lieferzeit + Puffer + Bestellrhythmus, abzüglich frei und Zulauf; auf Verpackungseinheit gerundet")}><span>{t("Vorschlag")}</span></Tip></th>
                <th className="h-9 pl-3 pr-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {q.isLoading && Array.from({ length: 8 }, (_, i) => <tr key={i} className="border-b border-border"><td colSpan={9} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td></tr>)}
              {items.map((r) => (
                <ReachRow key={r.productId} r={r} windowDays={shownWindow} picked={picked.has(r.productId)} onPick={(on) => toggle(r, on)} qty={qtyOf(r)} onQty={(v) => setQty(r, v)} onEdit={() => setEditing(r)} />
              ))}
            </tbody>
          </table>
        </div>
        {!q.isLoading && items.length === 0 && (
          <EmptyState icon={<SlidersHorizontal className="size-5" />} title={filter === "action" ? t("Nichts nachzubestellen") : t("Keine Artikel für diesen Filter")}>
            {filter === "action" ? t("Alle Artikel reichen beim aktuellen Absatz über Lieferzeit und Puffer hinaus.") : t("Passe Filter oder Suche an.")}
          </EmptyState>
        )}
        {(q.data?.total ?? 0) > PAGE_SIZE && (
          <div className="flex items-center gap-3 px-4 py-2.5 border-t border-border text-[12.5px] text-fg-muted">
            <span className="tabular-nums">{fmtNumber((page - 1) * PAGE_SIZE + 1)}–{fmtNumber(Math.min(q.data!.total, page * PAGE_SIZE))} {t("von")} {fmtNumber(q.data!.total)}</span>
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t("‹ Zurück")}</Button>
              <span className="tabular-nums px-1">{page} / {fmtNumber(pages)}</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t("Weiter ›")}</Button>
            </div>
          </div>
        )}
      </Card>
      <p className="text-[12px] text-fg-subtle">
        {t("Absatz aus euren Aufträgen (ohne Stornos, inkl. übernommener Shop-Historie), stündlich aktualisiert. Bestellpunkt = Lieferzeit + Puffer. Werte je Artikel (Lieferzeit, Verpackungseinheit, Mindestmenge, Lieferant) über")} <SlidersHorizontal className="inline size-3" /> {t("in der Zeile.")}
      </p>

      {picked.size > 0 && mayInbound && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface shadow-xl px-4 py-3 animate-pop-in">
          <span className="text-[13px]"><b className="tabular-nums">{picked.size}</b> {picked.size === 1 ? t("Artikel") : t("Artikel")} · <b className="tabular-nums">{fmtNumber(pickedUnits)}</b> {t("Stück")}</span>
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Map())}>{t("Auswahl aufheben")}</Button>
          {purchasing ? (
            <>
              <Button className="ml-auto" icon={<PackagePlus className="size-4" />} onClick={toInbound}>{t("Nur Anlieferung anmelden")}</Button>
              <Button variant="primary" icon={<ClipboardList className="size-4" />} loading={order.isPending} onClick={() => order.mutate(null)}>{t("Bestellung erstellen")}</Button>
            </>
          ) : (
            <Button className="ml-auto" variant="primary" icon={<PackagePlus className="size-4" />} onClick={toInbound}>{t("Anlieferung anmelden")}</Button>
          )}
        </div>
      )}

      {askSupplier && <SupplierPickDialog skus={askSupplier} loading={order.isPending} onPick={(id) => order.mutate(id)} onClose={() => setAskSupplier(null)} />}
      {s && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={s} />}
      {editing && <ProductReplenishmentDialog item={editing} defaults={s!} suppliers={q.data?.suppliers ?? []} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Th({ label, onClick, active, tip }: { label: string; onClick: () => void; active: boolean; tip?: string }) {
  const btn = (
    <button onClick={onClick} className={cx("inline-flex items-center gap-1 hover:text-fg", active && "text-fg")}>
      {t(label)} <ArrowUpDown className="size-3" />
    </button>
  );
  return <th className="h-9 px-3 font-medium">{tip ? <Tip content={tip}>{btn}</Tip> : btn}</th>;
}

function ReachRow({ r, windowDays, picked, onPick, qty, onQty, onEdit }: { r: ReachItem; windowDays: number; picked: boolean; onPick: (on: boolean) => void; qty: number; onQty: (v: number) => void; onEdit: () => void }) {
  const m = STATUS_META[r.status];
  const target = Math.max(1, (r.leadDays + r.safetyDays) * 2);
  const pct = r.daysLeft == null ? 0 : Math.min(100, (r.daysLeft / target) * 100);
  const actionable = r.status === "out" || r.status === "critical" || r.status === "reorder";
  return (
    <tr className={cx("border-b border-border last:border-0 hover:bg-surface-2/50", picked && "bg-surface-2/60")}>
      <td className="pl-4 pr-1 py-2.5">
        <input type="checkbox" aria-label={t("{sku} auswählen", { sku: r.sku })} checked={picked} onChange={(e) => onPick(e.target.checked)} className="accent-ink" />
      </td>
      <td className="px-3 py-2.5 min-w-[180px] max-w-[280px]">
        <Link to={`/products?open=${r.productId}`} className="font-medium hover:underline">{r.name}</Link>
        <div className="text-[11.5px] text-fg-muted"><span className="font-mono">{r.sku}</span>{r.supplier && <> · {r.supplier}</>}</div>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Sparkline values={r.weekly} />
          <span className="tabular-nums text-[13px]">{r.daily ? fmtNumber(r.daily) : "—"}<span className="text-fg-subtle text-[11px]">{t("/Tag")}</span></span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
        <div className="text-[13px]">{fmtNumber(r.soldWindow)} <span className="text-fg-subtle text-[11px]">{t("in")} {windowDays} T.</span></div>
        <div className="text-[11px] text-fg-subtle">{fmtNumber(r.sold90)} {t("in 90 T.")}</div>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(r.free)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {r.incoming ? (
          <Tip content={r.nextInbound?.at ? t("nächste Anlieferung erwartet {v}", { v: new Date(r.nextInbound.at).toLocaleDateString(intlLocale()) }) : t("angemeldet, noch nicht eingebucht")}>
            <Link to="/inbounds?status=announced" className="text-info hover:underline">+{fmtNumber(r.incoming)}</Link>
          </Tip>
        ) : <span className="text-fg-subtle">—</span>}
      </td>
      <td className="px-3 py-2.5 min-w-[150px]">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-12 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", m.bar)} style={{ width: `${pct}%` }} /></div>
          <span className="tabular-nums text-[12.5px]">{r.status === "out" ? "0 T." : days(r.daysLeft)}</span>
        </div>
        <div className="text-[11.5px] whitespace-nowrap mt-0.5">
          {r.orderInDays == null || r.status === "ignored" ? null
            : r.orderInDays < 0 ? <span className="text-danger font-medium">{t("bestellen: überfällig seit")} {-r.orderInDays} T.</span>
            : r.orderInDays === 0 ? <span className="text-danger font-medium">{t("bestellen: heute")}</span>
            : r.orderInDays <= 7 ? <span className="text-warning font-medium">{t("bestellen in")} {r.orderInDays} T.</span>
            : <span className="text-fg-subtle">{t("bestellen in")} {r.orderInDays} T.</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">
        {actionable || picked ? (
          <input value={qty || ""} onChange={(e) => onQty(Math.max(0, Math.round(Number(e.target.value.replace(/\D/g, "")) || 0)))} inputMode="numeric" placeholder="0" aria-label={t("Menge für {sku}", { sku: r.sku })} className="w-20 h-7 rounded-md border border-border bg-surface px-1.5 text-right text-[13px] tabular-nums font-semibold" />
        ) : <span className="text-fg-subtle">—</span>}
        {(r.packSize || r.minQty) && (actionable || picked) ? <div className="text-[10.5px] text-fg-subtle mt-0.5">{r.packSize ? t("VE {packSize}", { packSize: r.packSize }) : ""}{r.packSize && r.minQty ? " · " : ""}{r.minQty ? t("min. {minQty}", { minQty: r.minQty }) : ""}</div> : null}
      </td>
      <td className="pl-3 pr-4 py-2.5">
        <div className="flex items-center gap-2 justify-end">
          <Badge tone={m.tone}>{REACH_STATUS_LABEL[r.status]}</Badge>
          <button onClick={onEdit} className={cx("size-7 grid place-items-center rounded-md hover:bg-surface-2", r.custom ? "text-fg" : "text-fg-subtle")} aria-label={t("Nachbestell-Werte für {sku}", { sku: r.sku })} title={t("Lieferzeit, Verpackungseinheit, Mindestmenge, Lieferant")}>
            <SlidersHorizontal className="size-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  const w = 56, h = 18;
  const pts = values.map((v, i) => `${(i / Math.max(1, values.length - 1)) * w},${h - 1 - (v / max) * (h - 2)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-fg-muted shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function NumField({ label, value, onChange, placeholder, suffix, hint }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; suffix?: string; hint?: string }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-medium">{t(label)}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))} placeholder={placeholder} inputMode="numeric" suffix={suffix} className="mt-1" />
      {hint && <span className="block mt-1 text-[11.5px] text-fg-subtle">{t(hint)}</span>}
    </label>
  );
}

export function DialogShell({ open, onOpenChange, title, description, children }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; description: string; children: React.ReactNode }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-md rounded-2xl bg-surface border border-border shadow-2xl p-5 animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold">{t(title)}</Dialog.Title>
          <Dialog.Description className="text-[12.5px] text-fg-muted mt-1">{t(description)}</Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SettingsDialog({ open, onOpenChange, settings }: { open: boolean; onOpenChange: (o: boolean) => void; settings: ReplenishmentSettings }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState({ leadDays: String(settings.leadDays), safetyDays: String(settings.safetyDays), coverDays: String(settings.coverDays), overstockDays: String(settings.overstockDays), windowDays: settings.windowDays });
  useEffect(() => {
    if (open) setV({ leadDays: String(settings.leadDays), safetyDays: String(settings.safetyDays), coverDays: String(settings.coverDays), overstockDays: String(settings.overstockDays), windowDays: settings.windowDays });
  }, [open, settings]);
  const save = useMutation({
    mutationFn: () => api.stock.saveReplenishmentSettings(tenantId, { ...settings, leadDays: Number(v.leadDays || 0), safetyDays: Number(v.safetyDays || 0), coverDays: Math.max(1, Number(v.coverDays || 30)), overstockDays: Math.max(30, Number(v.overstockDays || 180)), windowDays: v.windowDays }),
    onSuccess: () => { toast.success("Gespeichert – Reichweite und Vorschläge sind neu berechnet"); onOpenChange(false); void qc.invalidateQueries({ queryKey: ["reach", tenantId] }); void qc.invalidateQueries({ queryKey: ["action-items", tenantId] }); },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Ungültige Werte") : "Speichern fehlgeschlagen"),
  });
  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title={t("Nachbestellung – Standardwerte")} description={t("Gelten für alle Artikel; je Artikel abweichend einstellbar.")}>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <NumField label={t("Lieferzeit")} value={v.leadDays} onChange={(x) => setV({ ...v, leadDays: x })} suffix={t("Tage")} hint={t("Bestellung bis Einlagerung")} />
        <NumField label={t("Sicherheitspuffer")} value={v.safetyDays} onChange={(x) => setV({ ...v, safetyDays: x })} suffix={t("Tage")} hint={t("für Schwankungen")} />
        <NumField label={t("Bestellung reicht für")} value={v.coverDays} onChange={(x) => setV({ ...v, coverDays: x })} suffix={t("Tage")} hint={t("Bestellrhythmus")} />
        <NumField label={t("Überbestand ab")} value={v.overstockDays} onChange={(x) => setV({ ...v, overstockDays: x })} suffix={t("Tage")} hint={t("Reichweite")} />
        <label className="block col-span-2">
          <span className="text-[12.5px] font-medium">{t("Absatz berechnen aus")}</span>
          <select value={v.windowDays} onChange={(e) => setV({ ...v, windowDays: Number(e.target.value) as ReplenishmentSettings["windowDays"] })} className="mt-1 w-full h-8 rounded-lg border border-border bg-surface px-2.5 text-[13px]">
            {[14, 30, 60, 90].map((d) => <option key={d} value={d}>{t("den letzten")} {d} {t("Tagen")}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={() => onOpenChange(false)}>{t("Abbrechen")}</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
      </div>
    </DialogShell>
  );
}

function ProductReplenishmentDialog({ item, defaults, suppliers, onClose }: { item: ReachItem; defaults: ReplenishmentSettings; suppliers: string[]; onClose: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState({ leadDays: item.leadDays !== defaults.leadDays ? String(item.leadDays) : "", safetyDays: item.safetyDays !== defaults.safetyDays ? String(item.safetyDays) : "", packSize: item.packSize ? String(item.packSize) : "", minQty: item.minQty ? String(item.minQty) : "", supplier: item.supplier ?? "", supplierSku: item.supplierSku ?? "", purchasePrice: item.purchasePrice != null ? String(item.purchasePrice).replace(".", ",") : "", ignore: item.status === "ignored" });
  const n = (x: string) => (x.trim() === "" ? null : Number(x));
  const body: ProductReplenishment = useMemo(() => ({ leadDays: n(v.leadDays), safetyDays: n(v.safetyDays), packSize: n(v.packSize), minQty: n(v.minQty), supplier: v.supplier.trim() || null, supplierSku: v.supplierSku.trim() || null, purchasePrice: v.purchasePrice.trim() ? Math.max(0, Number(v.purchasePrice.replace(",", ".")) || 0) : null, ignore: v.ignore }), [v]);
  const save = useMutation({
    mutationFn: () => api.products.setReplenishment(tenantId, item.productId, body),
    onSuccess: () => { toast.success(`${item.sku}: Nachbestell-Werte gespeichert`); onClose(); void qc.invalidateQueries({ queryKey: ["reach", tenantId] }); void qc.invalidateQueries({ queryKey: ["action-items", tenantId] }); },
    onError: () => toast.error("Speichern fehlgeschlagen – Werte prüfen"),
  });
  return (
    <DialogShell open onOpenChange={(o) => !o && onClose()} title={t("Nachbestellung: {name}", { name: item.name })} description={t("{sku} · leere Felder = Standard (Lieferzeit {leadDays} T., Puffer {safetyDays} T.)", { sku: item.sku, leadDays: defaults.leadDays, safetyDays: defaults.safetyDays })}>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <NumField label={t("Lieferzeit")} value={v.leadDays} onChange={(x) => setV({ ...v, leadDays: x })} placeholder={String(defaults.leadDays)} suffix={t("Tage")} />
        <NumField label={t("Sicherheitspuffer")} value={v.safetyDays} onChange={(x) => setV({ ...v, safetyDays: x })} placeholder={String(defaults.safetyDays)} suffix={t("Tage")} />
        <NumField label={t("Verpackungseinheit")} value={v.packSize} onChange={(x) => setV({ ...v, packSize: x })} placeholder="1" suffix={t("Stück")} hint={t("Vorschlag wird aufgerundet")} />
        <NumField label={t("Mindestbestellmenge")} value={v.minQty} onChange={(x) => setV({ ...v, minQty: x })} placeholder="—" suffix={t("Stück")} />
        <label className="block col-span-2">
          <span className="text-[12.5px] font-medium">{t("Lieferant")}</span>
          <Input value={v.supplier} onChange={(e) => setV({ ...v, supplier: e.target.value })} list="reach-suppliers" placeholder={t("z. B. Manufaktur Lindner")} className="mt-1" />
          <datalist id="reach-suppliers">{suppliers.map((x) => <option key={x} value={x} />)}</datalist>
        </label>
        <label className="block">
          <span className="text-[12.5px] font-medium">{t("Artikelnummer beim Lieferanten")}</span>
          <Input value={v.supplierSku} onChange={(e) => setV({ ...v, supplierSku: e.target.value })} placeholder={t("steht auf der Bestellung")} className="mt-1 font-mono" />
        </label>
        <label className="block">
          <span className="text-[12.5px] font-medium">{t("Einkaufspreis je Stück (netto)")}</span>
          <Input value={v.purchasePrice} onChange={(e) => setV({ ...v, purchasePrice: e.target.value.replace(/[^\d,.]/g, "") })} placeholder={t("optional")} suffix={t("EUR")} inputMode="decimal" className="mt-1" />
        </label>
        <label className="col-span-2 flex items-start gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" checked={v.ignore} onChange={(e) => setV({ ...v, ignore: e.target.checked })} className="accent-ink mt-0.5" />
          <span>{t("Nicht nachbestellen")} <span className="block text-[12px] text-fg-subtle">{t("Auslaufartikel – keine Warnungen und Vorschläge mehr")}</span></span>
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose}>{t("Abbrechen")}</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
      </div>
    </DialogShell>
  );
}

/** Artikel ohne Lieferant: für diese Bestellung einen Lieferanten wählen (oder in den Artikel-Einstellungen hinterlegen) */
function SupplierPickDialog({ skus, loading, onPick, onClose }: { skus: string[]; loading: boolean; onPick: (supplierId: string) => void; onClose: () => void }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["suppliers", tenantId], queryFn: () => api.purchasing.suppliers(tenantId) });
  const [id, setId] = useState("");
  return (
    <DialogShell open onOpenChange={(o) => !o && onClose()} title={t("Lieferant wählen")} description={t("Für die Bestellung")}>
      <p className="text-[13px] text-fg-muted">{t("Für")} {skus.length === 1 ? t("diesen Artikel") : t("diese Artikel")} {t("ist kein Lieferant hinterlegt:")} <span className="font-mono text-fg">{skus.join(", ")}</span>{t(". Für welche Bestellung?")}</p>
      <p className="mt-1 text-[12px] text-fg-subtle">{t("Tipp: Lieferant am Artikel hinterlegen (Regler-Symbol in der Zeile) – dann ordnet Connect künftig automatisch zu.")}</p>
      <select value={id} onChange={(e) => setId(e.target.value)} className="mt-4 h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
        <option value="">{t("Lieferant wählen …")}</option>
        {q.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {q.data?.length === 0 && <p className="mt-2 text-[12px] text-fg-subtle">{t("Noch keine Lieferanten – unter Einkauf → Lieferanten anlegen.")}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose}>{t("Abbrechen")}</Button>
        <Button variant="primary" disabled={!id} loading={loading} onClick={() => onPick(id)}>{t("Bestellung erstellen")}</Button>
      </div>
    </DialogShell>
  );
}
